<template>
    <Progressbar v-if="loading" />
    <NetworkError v-else-if="!clue.first_name && errors" @tryAgain="getData" />
    <div class="w-full h-full bg-gray-200 dark:bg-base-100 rounded-tr-3xl xl:px-9 pt-6 lg:pt-10 pb-14 " v-else>
        <div class="container">
            <div class="flex justify-between items-center mb-6">
                <h3 class="flex items-center md:text-26 text-2xl dark:text-white text-biscay-700 font-semibold">
                    <i class="md:flex hidden w-2 h-2 dark:bg-white rounded-full bg-blue-700 ml-2"></i>
                    {{ __('Clue details') }}
                </h3>
                <div class="flex justify-end order-last">
                    <Dropdown labelClass="btn-outline !btn-sm">
                        <ul class="menu dropdown-content bg-base-100 rounded-box z-[2] w-36 p-2 shadow lg:!end-auto">
                            <li>
                                <router-link :to="{ name: 'user.clue.edit', params: { id: clue.id } }"
                                    :class="{'btn-disabled opacity-50': !can('clue.edit.all') && !can('clue.edit.self', clue.self && clue.status != 'converted')}">
                                    {{ __('Edit') }}
                                </router-link>
                            </li>
                            <li> <router-link :to="{ name: 'user.clue.convert', params: { id: clue.id } }"
                                    :class="{'btn-disabled opacity-50': clue.is_opportunity || (!can('clue.edit.all') && !can('clue.edit.self', clue.self && clue.status != 'converted'))}">
                                    {{ __('Lead conversion') }}
                                </router-link></li>
                            <li v-if="can('appointment.create') && !clue.is_opportunity">
                                <button @click="setAppointment('create')">
                                    {{ __('Create activity') }}
                                </button>
                            </li>
                        </ul>
                    </Dropdown>

                    <label @click="setItem(clue)" for="clue-delete-modal"
                        :class="{ 'btn-disabled': !can('clue.delete.all') && !can('clue.delete.self', clue.self && clue.status != 'converted') }"
                        class="btn btn-outline btn-error btn-sm mr-2">
                        {{ __('Delete') }}
                    </label>
                </div>
            </div>
            <div class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm p-4 mb-4"
                v-if="clue.person || clue.account || clue.opportunity || clue.appointment">
                <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                    <p class="text-gray-70" v-if="clue.person">{{ __('Person created') }}: <router-link
                            :to="{ name: 'user.person.show', params: { id: clue.person.id } }"
                            class="text-blue-500 hover:text-blue-600">{{
                                clue.person.name
                            }}</router-link></p>
                    <p class="text-gray-70" v-if="clue.account">{{ __('Account created') }}: <router-link
                            :to="{ name: 'user.account.show', params: { id: clue.account.id } }"
                            class="text-blue-500 hover:text-blue-600">{{
                                clue.account.name
                            }}</router-link></p>
                    <p class="text-gray-70" v-if="clue.opportunity">{{ __('Opportunity created') }}: <router-link
                            :to="{ name: 'user.opportunity.show', params: { id: clue.opportunity.id } }"
                            class="text-blue-500 hover:text-blue-600">{{
                                clue.opportunity.name
                            }}</router-link></p>
                    <p class="text-gray-70" v-if="clue.appointment">{{ __('Appointment created') }}: <router-link
                            :to="{ name: 'user.' + clue.appointment.type + '.show', params: { id: clue.appointment.id } }"
                            class="text-blue-500 hover:text-blue-600">{{
                                clue.appointment.subject
                            }}</router-link></p>
                </div>
            </div>
            <div class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse">
                <input type="checkbox" checked />
                <div class="collapse-title text-xl font-medium">
                    {{ __('personal information') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70">{{ __('Full name') }}: <span class="text-black dark:text-white">{{
                            (clue.salutation ? __(clue.salutation?.capitalize()) + ' ' : '') + clue.name
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Type') }}: <span class="text-black dark:text-white">{{
                            __(clue.type?.capitalize())
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Account name') }}: <span class="text-black dark:text-white">{{
                            clue.account_name || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Lead source') }}: <span class="text-black dark:text-white">{{
                            clue.lead_source_title || '---' }}</span></p>
                        <p class="text-gray-70" v-if="clue.emails.filter(item => item.value).length">{{ __('Email') }}:
                            <span class="text-black dark:text-white"
                                v-for="item in clue.emails.filter(item => item.value)">{{
                                    item.value
                                }}</span>
                        </p>
                        <p class="text-gray-70">{{ __('Website') }}: <span class="text-black dark:text-white">{{
                            clue.website || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('National number') }}: <span class="text-black dark:text-white">{{
                            clue.national_number || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Side') }}: <span class="text-black dark:text-white">{{
                            clue.title || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Status') }}: <span class="text-black dark:text-white">{{
                            __(clue.status?.replace('_', ' ')?.capitalize()) || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('User') }}: <span class="text-black dark:text-white">{{
                            clue.user }}</span></p>
                        <p class="text-gray-70">{{ __('Referred user') }}: <span class="text-black dark:text-white">{{
                            clue.report_to || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Created At') }}: <span class="text-black dark:text-white">{{
                            clue.created_at }}</span></p>
                        <p class="text-gray-70">{{ __('Update date') }}: <span class="text-black dark:text-white">{{
                            clue.updated_at }}</span></p>
                        <div class="divider h-auto col-span-full my-0"
                            v-if="clue.numbers.filter(item => item.value).length"></div>
                        <p class="text-gray-70" v-for="item in clue.numbers.filter(item => item.value)">{{
                            __(item.type.capitalize()) }}: <span class="text-black dark:text-white">{{ item.value
                                }}</span></p>
                        <div class="divider h-auto col-span-full my-0"></div>
                        <p class="text-gray-70 col-span-full">{{ __('Description') }}: <span
                                class="text-black dark:text-white">{{
                                    clue.description || '---' }}</span></p>
                    </div>
                </div>
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('More information') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70">{{ __('The level of attractiveness') }}: <span
                                class="text-black dark:text-white">{{
                                    __(clue.rating?.capitalize()) || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Title') }}: <span class="text-black dark:text-white">{{
                            clue.title || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Campaign') }}: <span class="text-black dark:text-white">{{
                            clue.campaign_name || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Industry') }}: <span class="text-black dark:text-white">{{
                            clue.industry || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Sub industry') }}: <span class="text-black dark:text-white">{{
                            clue.subindustry || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Referred by') }}: <span class="text-black dark:text-white">{{
                            clue.refered_by || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Date of birth') }}: <span class="text-black dark:text-white">{{
                            clue.birthdate || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Economic code') }}: <span class="text-black dark:text-white">{{
                            clue.tax_id || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Registration number') }}: <span
                                class="text-black dark:text-white">{{
                                    clue.registration_number || '---' }}</span></p>
                        <div class="divider h-auto col-span-full my-0" v-if="clue.message"></div>
                        <p class="text-gray-70 col-span-full" v-if="clue.message">{{ __('Ready message') }}: <span
                                class="text-black dark:text-white">{{
                                    clue.message || '---' }}</span></p>
                    </div>
                </div>
            </div>
            <div v-if="clue.logs?.length"
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Referral History') }}
                </div>
                <div class="collapse-content">
                    <div class="overflow-x-auto">
                        <table class="table table-xs md:table-sm xl:table-md">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{{ __('User') }}</th>
                                    <th>{{ __('Reference to') }}</th>
                                    <th>{{ __('Status') }}</th>
                                    <th>{{ __('Reference date') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in clue.logs">
                                    <th>{{ index + 1 }}</th>
                                    <th>{{ item.user }}</th>
                                    <td>{{ item.report_to }}</td>
                                    <td>
                                        {{ __(item.status?.replace('_', ' ')?.capitalize()) }}
                                    </td>
                                    <td>
                                        {{ item.created_at }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <!-- <div class="collapse-content">
                    <ul class="timeline timeline-snap-icon max-md:timeline-compact timeline-vertical">
                        <li v-for="(log,index) in clue.logs">
                            <div class="timeline-middle">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                                    class="text-primary h-5 w-5">
                                    <path fill-rule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                                        clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="mb-4" :class="[index % 2 == 0 ? 'timeline-end' : 'timeline-start']">
                                <small class="font-semibold">{{ log.created_at }}</small>
                                <div class="text-sm">{{ log.user }} <span class="text-warning text-xs" v-if="log.status">({{ __(log.status?.replace('_',' ')?.capitalize()) }})</span></div>
                            </div>
                           <hr class="bg-primary" />
                        </li>
                    </ul>
                </div> -->
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Address') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70">{{ __('Country') }}: <span class="text-black dark:text-white">{{
                            clue.billing.country || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('State') }}: <span class="text-black dark:text-white">{{
                            clue.billing.state || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('City') }}: <span class="text-black dark:text-white">{{
                            clue.billing.city || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Postal Code') }}: <span class="text-black dark:text-white">{{
                            clue.billing.postalcode || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Address') }}: <span class="text-black dark:text-white">{{
                            clue.billing.street || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Latitude') }}: <span class="text-black dark:text-white">{{
                            clue.billing.latitude || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Longitude') }}: <span class="text-black dark:text-white">{{
                            clue.billing.longitude || '---' }}</span></p>
                    </div>
                </div>
            </div>
            <div v-if="clue.social.length"
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Social') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70" v-for="(item, key) in clue.social">{{ __(key?.capitalize()) }}: <span
                                class="text-black dark:text-white">{{
                                    item || '---'
                                }}</span></p>
                    </div>
                </div>
            </div>
               <div v-if="clue.appointments?.length"
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Activities') }}
                </div>
                <div class="collapse-content">
                    <TableAppointment :appointments="clue.appointments" @setItem="setItem" />
                </div>
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Appendices') }}
                </div>
                <div class="collapse-content">
                    <div class="overflow-x-auto">
                        <table class="table table-xs md:table-sm xl:table-md">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{{ __('File title') }}</th>
                                    <th>{{ __('File type') }}</th>
                                    <th>{{ __('Status') }}</th>
                                    <th>{{ __('Action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in clue.attachments">
                                    <th>{{ index + 1 }}</th>
                                    <th>{{ item.name }}</th>
                                    <td>{{ item.mime_type }}</td>
                                    <td>
                                        {{ __(item.url ? 'Uploaded' : 'Uploading') }}
                                    </td>
                                    <td>
                                        <a :href="item.url" target="_blank" class="btn btn-outline btn-info btn-sm">
                                            {{ __('Show') }}
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="text-center" v-if="!clue.attachments.length">
                                <tr>
                                    <td colspan="5">{{ __('No file found to display') }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <Teleport to="#app">
        <input type="checkbox" id="add-appointment-modal" class="modal-toggle" />
        <div class="modal" for="add-appointment-modal">
            <div class="modal-box max-w-7xl bg-base-200 min-h-40" for="">
                <label @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</label>
                <AddEditAppointment :appointment="appointment" v-if="modal == 'add-appointment-modal'"
                    modal="add-appointment-modal" />
            </div>
        </div>
    </Teleport>

    <ModalDelete id="clue-delete-modal" :title='__("Delete clue")' :content='__("Are you sure to delete this clue?")'
        @remove="removeClue" />

    <ModalDelete id="appointment-delete-modal" :title="__('Delete appointment')"
        :content="__('Are you sure to delete this appointment?')" @remove="removeAppointmentItem" />
</template>
<script setup>

import { computed, onMounted, reactive, ref } from 'vue';
const { getClue, clue, route, loading, meta, errors, remove, removeClue,removeAppointment, appointment, loadingBtn } = useDashboard();
const setItem = (item) => Object.assign(remove, { id: item.id, single: true });
const modal = ref(null);
const setAppointment = () => {
    Object.assign(appointment, {
        parent_type: "clue",
        appointmentable_id: clue.id,
    });
    modal.value = 'add-appointment-modal';
    document.getElementById(modal.value).checked = true;
}
const removeAppointmentItem = () => {
    removeAppointment('appointment-delete-modal').then((res) => {
        if (res && !res.error) getData();
    });
}
watch(progressDialog, (value) => {
    if (modal.value && !value) {
        modal.value = null;
        getData();
    }
})
const hideModal = () => {
    document.getElementById(modal.value).checked = false;
    modal.value = null;
}
const getData = () => {
    getClue();
}
onMounted(() => {
    getData();
});
</script>